import React from 'react';
import { MobileNavigation } from './MobileNavigation';
import { 
  LayoutDashboard, 
  Router, 
  Users, 
  CreditCard, 
  Headphones, 
  Ticket,
  UserCog,
  Palette,
  HardDrive,
  HelpCircle,
} from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import {
  canAccessAccounts,
  canAccessPortalBranding,
  END_USER_HIDDEN_NAV_IDS,
} from '../lib/accountRoles';

interface NavigationProps {
  currentPage: string;
  onPageChange: (page: string) => void;
}

const baseNavigationItems = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'routers', label: 'Routers', icon: Router },
  { id: 'customers', label: 'Users', icon: Users },
  { id: 'service-plans', label: 'Service Plans', icon: CreditCard },
  { id: 'vouchers', label: 'Vouchers', icon: Ticket },
];

const accountsNavEntry = { id: 'accounts', label: 'Accounts', icon: UserCog };
const portalBrandingNavEntry = {
  id: 'portal-branding',
  label: 'Portal branding',
  icon: Palette,
};
const firmwareNavEntry = { id: 'firmware', label: 'Firmware', icon: HardDrive };
const faqNavEntry = { id: 'faq', label: 'FAQ', icon: HelpCircle };

export const Navigation = React.memo(function Navigation({ currentPage, onPageChange }: NavigationProps) {
  const { user } = useAuth();

  const showAccountsNav = canAccessAccounts(user?.role, user?.parentAccountId);
  const supportNavEntry = { id: 'support-tickets', label: 'Support', icon: Headphones };
  const showPortalBrandingNav = canAccessPortalBranding(user?.role, user?.parentAccountId);

  const navigationItems = [
    ...baseNavigationItems.filter((item) => {
      if (user?.role === 'end_user') {
        return !END_USER_HIDDEN_NAV_IDS.includes(item.id as (typeof END_USER_HIDDEN_NAV_IDS)[number]);
      }
      return true;
    }),
    ...(showPortalBrandingNav ? [portalBrandingNavEntry] : []),
    supportNavEntry,
    ...(showAccountsNav ? [accountsNavEntry] : []),
    firmwareNavEntry,
    faqNavEntry,
  ];
  return (
    <>
      {/* Mobile Navigation */}
      <div className="md:hidden mb-4">
        <MobileNavigation currentPage={currentPage} onPageChange={onPageChange} />
      </div>
      
      {/* Desktop Navigation */}
      <nav className="hidden md:block mb-6">
        <div className="flex flex-wrap gap-1">
          {navigationItems.map((item) => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant={currentPage === item.id ? 'default' : 'outline'}
                size="default"
                onClick={() => onPageChange(item.id)}
                className={`flex items-center space-x-2 transition-all duration-200 text-base ${
                  currentPage === item.id 
                    ? '' 
                    : 'hover:border-[var(--sw-brand)] hover:text-[var(--sw-brand)]'
                }`}
                style={currentPage === item.id ? {
                  backgroundColor: 'var(--sw-brand)',
                  borderColor: 'var(--sw-brand)',
                  color: 'white'
                } : {}}
              >
                <Icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </nav>
    </>
  );
});